import {
  BACKGROUND_SELECTOR,
  applyBackgroundsForElement
} from './lazy-loading-backgrounds'

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='

const DEFAULT_IMAGE_SELECTOR = 'img'
const RESIZE_DEBOUNCE_MS = 150

const IMAGE_INTERSECTION_OPTIONS = { rootMargin: '100px 0px', threshold: 0 }
const BACKGROUND_INTERSECTION_OPTIONS = {
  rootMargin: '150px 0px',
  threshold: 0
}

export default class LazyImageLoader {
  constructor(imageSelector = DEFAULT_IMAGE_SELECTOR) {
    this.imageSelector = imageSelector
    this.supportsIntersectionObserver = 'IntersectionObserver' in window
    this.imageObserver = null
    this.backgroundObserver = null
    this.mutationObserver = null
    this.resizeTimer = null
    this.enabled = false

    // Stable references for event listeners
    this.handleResize = this.handleResize.bind(this)
  }

  init() {
    this.enabled = this.isPageOptIn()
    window.addEventListener('resize', this.handleResize)

    if (!this.enabled) {
      this.loadEverythingEagerly()
      return
    }

    this.scheduleImages()
    this.scheduleBackgrounds()
    this.scheduleMutations()
  }

  isPageOptIn() {
    return document.body?.dataset.lazyImages === 'true'
  }

  loadEverythingEagerly() {
    document.querySelectorAll(this.imageSelector).forEach((img) => {
      if (
        this.shouldSkipImage(img) ||
        (!img.dataset.src && !img.dataset.srcset)
      )
        return
      this.swapImageToFinalSrc(img)
    })
    document.querySelectorAll(BACKGROUND_SELECTOR).forEach((el) => {
      applyBackgroundsForElement(el)
    })
  }

  scheduleImages() {
    document
      .querySelectorAll(this.imageSelector)
      .forEach((img) => this.prepareAndObserveImage(img))
  }

  prepareAndObserveImage(img) {
    const { dataset } = img
    if (dataset.lazyManaged === 'true' || this.shouldSkipImage(img)) return

    const liveSrc = img.getAttribute('src')
    if (liveSrc && !dataset.src && liveSrc !== TRANSPARENT_PIXEL) {
      dataset.src = liveSrc
    }
    const liveSrcset = img.getAttribute('srcset')
    if (liveSrcset && !dataset.srcset) {
      dataset.srcset = liveSrcset
    }

    if (!dataset.src && !dataset.srcset) return

    if (dataset.src) img.setAttribute('src', TRANSPARENT_PIXEL)
    if (dataset.srcset) img.removeAttribute('srcset')

    img.setAttribute('decoding', 'async')
    img.classList.add('lazy')
    dataset.lazyManaged = 'true'

    if (!this.supportsIntersectionObserver) {
      this.swapImageToFinalSrc(img)
      return
    }

    this.ensureObserver(
      'imageObserver',
      IMAGE_INTERSECTION_OPTIONS,
      this.swapImageToFinalSrc
    )
    this.imageObserver.observe(img)
  }

  swapImageToFinalSrc(img) {
    if (img.dataset.src) img.setAttribute('src', img.dataset.src)
    if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset)
    img.classList.remove('lazy')
    img.classList.add('lazy-loaded')
  }

  shouldSkipImage(img) {
    return img.dataset.lazyExclude === 'true' || !!img.closest('noscript')
  }

  scheduleBackgrounds() {
    document
      .querySelectorAll(BACKGROUND_SELECTOR)
      .forEach((el) => this.observeBackground(el))
  }

  observeBackground(el) {
    if (el.dataset.lazyBgManaged === 'true') return
    el.dataset.lazyBgManaged = 'true'

    if (!this.supportsIntersectionObserver) {
      this.swapBackground(el)
      return
    }

    this.ensureObserver(
      'backgroundObserver',
      BACKGROUND_INTERSECTION_OPTIONS,
      this.swapBackground
    )
    this.backgroundObserver.observe(el)
  }

  swapBackground(el) {
    if (applyBackgroundsForElement(el)) {
      el.dataset.lazyBgLoaded = 'true'
    }
  }

  ensureObserver(observerKey, options, swapFn) {
    if (this[observerKey]) return
    this[observerKey] = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        swapFn.call(this, entry.target)
        observer.unobserve(entry.target)
      })
    }, options)
  }

  scheduleMutations() {
    if (!document.body) return
    this.mutationObserver = new MutationObserver((mutations) =>
      this.handleMutations(mutations)
    )
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  handleMutations(mutations) {
    const imgSelector = this.imageSelector
    const bgSelector = BACKGROUND_SELECTOR

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return

        // Process images cleanly
        if (node.matches?.(imgSelector)) this.prepareAndObserveImage(node)
        if (node.querySelectorAll) {
          node
            .querySelectorAll(imgSelector)
            .forEach((img) => this.prepareAndObserveImage(img))
        }

        // Process background containers cleanly
        if (node.matches?.(bgSelector)) this.observeBackground(node)
        if (node.querySelectorAll) {
          node
            .querySelectorAll(bgSelector)
            .forEach((bg) => this.observeBackground(bg))
        }
      })
    })
  }

  handleResize() {
    if (this.resizeTimer) window.clearTimeout(this.resizeTimer)
    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = null
      if (this.enabled) {
        document
          .querySelectorAll('[data-lazy-bg-loaded="true"]')
          .forEach((el) => {
            applyBackgroundsForElement(el)
          })
        return
      }
      document.querySelectorAll(BACKGROUND_SELECTOR).forEach((el) => {
        applyBackgroundsForElement(el)
      })
    }, RESIZE_DEBOUNCE_MS)
  }

  destroy() {
    this.imageObserver?.disconnect()
    this.backgroundObserver?.disconnect()
    this.mutationObserver?.disconnect()
    this.imageObserver = null
    this.backgroundObserver = null
    this.mutationObserver = null

    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
  }
}
